import { UserExperiments } from '@acp/shared';
import { Link } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useNavLink } from '../hooks/use-nav-link';
import { useExperiment } from '../hooks/use-experiment';

function ExperimentLinkRefFunction(
    {
        experimentName,
        path,
        children,
        withExperiment,
        withoutExperiment,
        ...linkProps
    }: Omit<React.ComponentPropsWithoutRef<typeof Link>, 'href'> & {
        path?: string;
        experimentName: keyof UserExperiments;
        withExperiment: {
            isExternal: boolean;
            to: string;
        };
        withoutExperiment: {
            isExternal: boolean;
            to: string;
        };
    },
    ref: React.ForwardedRef<HTMLAnchorElement>
) {
    const experiments = useExperiment();

    const linkDef = useMemo(() => {
        if (experiments.hasExperiment(experimentName)) {
            return withExperiment;
        }

        return withoutExperiment;
    }, [experiments.userExperiments, experimentName, withExperiment, withoutExperiment]);

    const transformedHref = useNavLink({
        to: linkDef.to,
        isExternal: true,
    });

    if (linkDef.isExternal) {
        return (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Link
                ref={ref}
                href={transformedHref}
                _hover={{ textDecoration: 'none' }}
                {...linkProps}
            >
                {children}
            </Link>
        );
    }

    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <Link
            as={RouterLink}
            ref={ref}
            to={linkDef.to}
            _hover={{ textDecoration: 'none' }}
            {...linkProps}
        >
            {children}
        </Link>
    );
}

export const ExperimentLink = React.forwardRef(ExperimentLinkRefFunction);
